import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useIntercom } from 'react-use-intercom';

import {
  COUNTRY_CODE_MEXICO,
  COUNTRY_CODE_SPAIN,
  COUNTRY_CODE_US,
  DEFAULT_COMPANY,
  DEFAULT_COMPANY_DATA,
  DEFAULT_HOLDING,
  SELECTED_COMPANY,
  SELECTED_HOLDING
} from '../../constants/constants';
import { INTERCOM_SET_LANGUAGE_EVENT } from '../../constants/intercom';
import { GENERIC_RELOAD_MODAL } from '../../constants/modal';
import { PLAN_TYPE_FR } from '../../constants/plan';
import { metricsData } from '../../handlers/dataHandlers';
import { IChangeBusinessDataResponse, ICompanyData, IUserCompany, IUserHolding } from '../../interfaces/businessData';
import { ICompanyContext, IDomainResponse, IFlexibleRemunerationPayRollCycles } from '../../interfaces/company';
import { IMembersToAddResponse, IUserDataResponse } from '../../interfaces/responseInterface';
import Companies from '../../services/api/collaborators/collaborators';
import Company from '../../services/api/companies/companies';
import Holding from '../../services/api/holdings/holdings';
import Users from '../../services/api/users/users';
import { getCurrentDate } from '../../utils/datetime/datetime';
import { getUserProps, sendUserPropsToGtm } from '../../utils/gtm/tagManager';
import { getFromLocalStorage, getUsernameFromEmail, toLocalStorage } from '../../utils/utils';
import { SessionContext } from '../auth/authContext';
import { ModalContext } from '../modal/modalContext';

const contextDefaultValues: ICompanyContext = {
  company: DEFAULT_COMPANY,
  companyData: DEFAULT_COMPANY_DATA,
  dataOrigin: 'company',
  hasSendMassEmail: true,
  holding: DEFAULT_HOLDING,
  holdingList: [],
  isLoadingCompanyData: false,
  username: '',
  locale: '',
  firstLogin: false,
  slackIntegration: false,
  companyDomains: {},
  errorGettingDomains: false,
  companyPlansType: '',
  companyRFCycles: undefined,
  isLoadingRFCycles: false,
  errorGettingRFCycles: false,
  isSpain: undefined,
  isMexico: undefined,
  isUSA: undefined,
  setUserCompany: () => {
    return;
  },
  setUserHolding: () => {
    return;
  },
  setUserCompanyAndHolding: () => {
    return;
  },
  setUserDataOrigin: () => {
    return;
  },
  setUserHasSendMassEmail: () => {
    return;
  },
  setUserLocale: () => {
    return;
  },
  updateUserHolding: () => {
    return;
  },
  updateUserCompany: () => {
    return;
  },
  setUserFirstLogin: () => {
    return;
  },
  changeSlackIntegrationState: () => {
    return;
  },
  callUpdateDomains: () => {
    return;
  },
  handleCompanyPlansType: () => {
    return;
  },
  fetchCompanyCycles: () => {
    return;
  }
};

export const CompanyContext = createContext<ICompanyContext>(contextDefaultValues);

const CompanyContextProvider = ({ children }: React.PropsWithChildren) => {
  const [company, setCompany] = useState<IUserCompany>(DEFAULT_COMPANY);
  const [companyData, setCompanyData] = useState<ICompanyData>(DEFAULT_COMPANY_DATA);
  const [isLoadingCompanyData, setIsLoadingCompanyData] = useState<boolean>(false);
  const [username, setUsername] = useState<string>('');
  const [holding, setHolding] = useState<IUserHolding>(DEFAULT_HOLDING);
  const [holdingList, setHoldingList] = useState<IUserHolding[]>([]);
  const [dataOrigin, setDataOrigin] = useState<'company' | 'holding'>('company');
  const [hasSendMassEmail, setHasSendMassEmail] = useState<boolean>(true);
  const [locale, setLocale] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [firstLogin, setFirstLogin] = useState<boolean>(false);
  const [slackIntegration, setSlackIntegration] = useState<boolean | null>(null);
  const [companyDomains, setCompanyDomains] = useState<IDomainResponse>({});
  const [errorGettingDomains, setErrorGettingDomains] = useState<boolean>(false);
  const [updateDomains, setUpdateDomains] = useState<boolean>(false);
  const [companyPlansType, setCompanyPlansType] = useState(contextDefaultValues.companyPlansType);
  const [companyRFCycles, setCompanyRFCycles] = useState<IFlexibleRemunerationPayRollCycles | undefined>();
  const [isLoadingRFCycles, setIsLoadingRFCycles] = useState<boolean>(false);
  const [errorGettingRFCycles, setErrorGettingRFCycles] = useState<boolean>(false);
  const [isSpain, setIsSpain] = useState<boolean | undefined>(undefined);
  const [isMexico, setIsMexico] = useState<boolean | undefined>(undefined);
  const [isUSA, setIsUSA] = useState<boolean | undefined>(undefined);

  const intercomSessionRef = useRef<number>(0);

  const { update, trackEvent, boot, shutdown } = useIntercom();
  const { isLoggedIn, setUserAttributes } = useContext(SessionContext);
  const { handleOpenModal } = useContext(ModalContext);

  const { i18n } = useTranslation();

  const isValidCompany = useCallback(() => {
    return company.id !== '';
  }, [company]);

  const updateUserAttributes = useCallback(
    async (response: IUserDataResponse) => {
      setUserAttributes({
        name: response.user?.name || '',
        lastName: response.user?.lastName || '',
        email: response.user.email,
        phone: response.user?.phone ?? '',
        image: response.user?.image ?? ''
      });
    },
    [setUserAttributes]
  );

  const bootIntercom = async (userEmail: string, externalId?: string) => {
    intercomSessionRef.current += 1;
    const currentSession = intercomSessionRef.current;

    if (!externalId) {
      boot();
      return;
    }

    try {
      const token = await Users.getIntercomToken();
      if (intercomSessionRef.current !== currentSession) {
        return;
      }
      if (!token) {
        boot();
        return;
      }
      boot({ email: userEmail, userId: externalId, customAttributes: { intercom_user_jwt: token } });
    } catch {
      if (intercomSessionRef.current === currentSession) {
        boot();
      }
    }
  };

  useEffect(() => {
    const fetchUserAttributes = async () => {
      try {
        shutdown();
        const response = await Users.getUserAttributes();
        updateUserAttributes(response);
        const { companiesIdList, companiesRolesList, externalIdList } = getUserProps(response.user.holdings);
        const userEmail = response.user.email;
        if (response.user.name) {
          setUsername(response.user.name.concat(' ', response.user.lastName));
        } else {
          setUsername(getUsernameFromEmail(userEmail));
        }

        const preselectedHolding = getFromLocalStorage(SELECTED_HOLDING);
        const selectedHolding =
          response.user.holdings.find((holding) => holding.id === Number(preselectedHolding)) ||
          response.user.holdings[0];

        setEmail(userEmail);
        setHoldingList(response.user.holdings);
        setUserHolding(selectedHolding);
        setUserLocale(response.user.locale);
        setFirstLogin(!response.user.hasLoggedIn);
        sendUserPropsToGtm(
          companiesIdList,
          companiesRolesList,
          email || userEmail,
          externalIdList,
          response.user.hasNewExperience
        );
        await bootIntercom(userEmail, response.user.externalId);
      } catch {
        handleOpenModal(GENERIC_RELOAD_MODAL);
      }
    };

    if (isLoggedIn) {
      fetchUserAttributes();
    } else {
      setHoldingList([]);
      setUserHolding(DEFAULT_HOLDING);
      setUserCompany(DEFAULT_COMPANY);
      setCompanyData(DEFAULT_COMPANY_DATA);
      setEmail('');
      setUsername('');
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        if (isValidCompany()) {
          setIsLoadingCompanyData(true);
          setCompanyRFCycles(undefined);
          setCompanyPlansType(undefined);
          const response = await Companies.getCompanyInfo(company.id, locale);
          setIntercomData(response.locale);
          setCompanyData(metricsData(response));
          setHasSendMassEmail(!response.sendEmail);
          setIsLoadingCompanyData(false);
          setSlackIntegration(response.integrations.slack);
          toLocalStorage(SELECTED_COMPANY, company.id);
          toLocalStorage(SELECTED_HOLDING, holding.id.toString());
          setIsSpain(response.countryCode.toLowerCase() === COUNTRY_CODE_SPAIN);
          setIsMexico(response.countryCode.toLowerCase() === COUNTRY_CODE_MEXICO);
          setIsUSA(response.countryCode.toLowerCase() === COUNTRY_CODE_US);
        }
      } catch {
        setIsLoadingCompanyData(false);
        handleOpenModal();
      }
    };

    fetchCompany();
  }, [company, handleOpenModal]);

  useEffect(() => {
    setUpdateDomains(false);
    if (isValidCompany()) {
      setErrorGettingDomains(false);
      const fetchCompanyDomains = async () => {
        try {
          const response = await Company.getDomains(company.id);
          setCompanyDomains(response);
        } catch {
          setErrorGettingDomains(true);
        }
      };

      fetchCompanyDomains();
    }
  }, [company, isValidCompany, updateDomains]);

  const setIntercomData = (countryCode: string) => {
    const locale = countryCode === 'pt' ? 'pt-BR' : countryCode;
    update({ languageOverride: locale });
    const metadata = { locale };
    trackEvent(INTERCOM_SET_LANGUAGE_EVENT, metadata);
  };

  const loadMergeIntegration = async () => {
    try {
      let response: IMembersToAddResponse;
      if (holding && holding.id > 0) {
        response = await Holding.getMembersToAdd(holding.id);
        if (response.pendingMembers.length > 0) {
          holding.hasMergeIntegration = true;
          holding.hasPendingMembers = true;
        }
        if (response.integrationName) {
          holding.mergeHRSystem = response.integrationName;
          holding.hasMergeIntegration = true;
        } else {
          holding.hasMergeIntegration = false;
        }
      }
    } catch {
      holding.hasMergeIntegration = false;
    }
  };

  const setUserCompany = useCallback((selectedCompany: IUserCompany) => {
    setCompany(selectedCompany);
  }, []);

  const setUserDataOrigin = useCallback((selectedDataOrigin: 'company' | 'holding') => {
    setDataOrigin(selectedDataOrigin);
  }, []);

  const setUserHasSendMassEmail = useCallback((didSendMassEmail: boolean) => {
    setHasSendMassEmail(didSendMassEmail);
  }, []);

  const changeSlackIntegrationState = useCallback((slackState: boolean) => {
    setSlackIntegration(slackState);
  }, []);

  const callUpdateDomains = useCallback((update: boolean) => {
    setUpdateDomains(update);
  }, []);

  const setUserHolding = useCallback(
    (selectedHolding: IUserHolding) => {
      setHolding(selectedHolding);
      const preselectedCompany = getFromLocalStorage(SELECTED_COMPANY);
      const selectedCompany =
        selectedHolding.companies.find((company) => company.id === preselectedCompany) || selectedHolding.companies[0];
      setUserCompany(selectedCompany);
    },
    [setUserCompany]
  );

  const setUserCompanyAndHolding = useCallback((selectedCompany: IUserCompany, selectedHolding: IUserHolding) => {
    setCompany(selectedCompany);
    setHolding(selectedHolding);
  }, []);

  const setUserLocale = useCallback((currentLocale: string) => {
    setLocale(currentLocale);
  }, []);

  const setUserFirstLogin = useCallback(() => {
    setFirstLogin(false);
  }, []);

  const updateUserHolding = useCallback(
    (holdingData: IChangeBusinessDataResponse) => {
      const holdingIndex = holdingList.findIndex((holding) => holding.id === Number(holdingData.id));
      if (holdingIndex >= 0) {
        const updatedHolding = { ...holdingList[holdingIndex], name: holdingData.name, image: holdingData.image };
        const updatedHoldingList = [
          ...holdingList.slice(0, holdingIndex),
          updatedHolding,
          ...holdingList.slice(holdingIndex + 1)
        ];
        if (updatedHolding.id === holding.id) {
          setHolding(updatedHolding);
        }
        setHoldingList(updatedHoldingList);
      }
    },
    [holdingList, holding]
  );

  const updateUserCompany = useCallback(
    (companyData: IChangeBusinessDataResponse) => {
      const holdingIndex = holdingList.findIndex((holdingItem) => holdingItem.id === holding.id);
      const companyIndex = holdingList[holdingIndex].companies.findIndex((company) => company.id === companyData.id);

      if (holdingIndex < 0 || companyIndex < 0) {
        return;
      }

      const today = getCurrentDate();

      const updatedCompany = {
        ...holdingList[holdingIndex].companies[companyIndex],
        name: companyData.name,
        image: companyData.image,
        updatedAt: today
      };
      const updatedCompanyList = [
        ...holdingList[holdingIndex].companies.slice(0, companyIndex),
        updatedCompany,
        ...holdingList[holdingIndex].companies.slice(companyIndex + 1)
      ];
      const updatedHolding = { ...holdingList[holdingIndex], companies: updatedCompanyList };
      const updatedHoldingList = [
        ...holdingList.slice(0, holdingIndex),
        updatedHolding,
        ...holdingList.slice(holdingIndex + 1)
      ];

      if (updatedCompany.id === company.id) {
        setCompany(updatedCompany);
      }
      setHolding(updatedHolding);
      setHoldingList(updatedHoldingList);
    },
    [holdingList, holding, company]
  );

  const handleCompanyPlansType = useCallback((plansType: string) => {
    setCompanyPlansType(plansType);
  }, []);

  const fetchCompanyCycles = useCallback(async () => {
    setErrorGettingRFCycles(false);
    setIsLoadingRFCycles(true);
    setCompanyRFCycles(undefined);
    try {
      const response = await Company.getCompanyFlexibleRemunerationPayRollCycles(companyData.id);
      setCompanyRFCycles(response);
    } catch {
      setErrorGettingRFCycles(true);
    } finally {
      setIsLoadingRFCycles(false);
    }
  }, [companyData]);

  useEffect(() => {
    if (companyPlansType === PLAN_TYPE_FR && companyData.id && companyData.id === company.id && !isLoadingCompanyData) {
      fetchCompanyCycles();
    }
  }, [companyPlansType, fetchCompanyCycles, companyData, isLoadingCompanyData, company]);

  useEffect(() => {
    i18n.changeLanguage(locale);
  }, [locale, i18n]);

  useEffect(() => {
    loadMergeIntegration();
  }, [holding]);

  const value = useMemo(
    () => ({
      company,
      companyData,
      dataOrigin,
      hasSendMassEmail,
      holding,
      holdingList,
      isLoadingCompanyData,
      locale,
      username,
      firstLogin,
      slackIntegration,
      companyDomains,
      errorGettingDomains,
      companyPlansType,
      companyRFCycles,
      isLoadingRFCycles,
      errorGettingRFCycles,
      isSpain,
      isMexico,
      isUSA,
      setUserCompany,
      setUserHolding,
      setUserCompanyAndHolding,
      setUserDataOrigin,
      setUserHasSendMassEmail,
      setUserLocale,
      setUserFirstLogin,
      updateUserHolding,
      updateUserCompany,
      changeSlackIntegrationState,
      callUpdateDomains,
      handleCompanyPlansType,
      fetchCompanyCycles
    }),
    [
      company,
      companyData,
      dataOrigin,
      hasSendMassEmail,
      holding,
      holdingList,
      isLoadingCompanyData,
      locale,
      username,
      firstLogin,
      slackIntegration,
      companyDomains,
      errorGettingDomains,
      companyPlansType,
      companyRFCycles,
      isLoadingRFCycles,
      errorGettingRFCycles,
      isSpain,
      isMexico,
      isUSA,
      setUserCompany,
      setUserHolding,
      setUserCompanyAndHolding,
      setUserDataOrigin,
      setUserHasSendMassEmail,
      setUserLocale,
      setUserFirstLogin,
      updateUserHolding,
      updateUserCompany,
      changeSlackIntegrationState,
      callUpdateDomains,
      handleCompanyPlansType,
      fetchCompanyCycles
    ]
  );

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
};

export default CompanyContextProvider;
